import React from 'react';
import { Modal, Table, Button, Space, Tag, Statistic, Row, Col, App } from 'antd';
import { DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { downloadFile, buildDownloadUrl } from '@/utils/download';
// import type { UserImportVO } from '@/services/ant-design-pro/typings';

interface UserImportResultModalProps {
  visible: boolean;
  onClose: () => void;
  importResult: any | null;
}

const UserImportResultModal: React.FC<UserImportResultModalProps> = ({
  visible,
  onClose,
  importResult,
}) => {
  const { message } = App.useApp();
  if (!importResult) return null;

  const { numberSuccesses = 0, numberFail = 0, succeedList = [], storageVO } = importResult;

  // 成功导入的用户列表列定义
  const successColumns = [
    {
      title: '用户名',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 100,
      render: (nickname: string) => nickname || '-',
    },
    {
      title: '性别',
      dataIndex: 'sex',
      key: 'sex',
      width: 80,
      render: (sex: string) => {
        if (!sex) return '-';
        const genderMap: { [key: string]: { text: string; color: string } } = {
          '男': { text: '男', color: 'blue' },
          '女': { text: '女', color: 'pink' },
          '未知': { text: '未知', color: 'default' },
          '1': { text: '男', color: 'blue' },
          '2': { text: '女', color: 'pink' },
          '3': { text: '未知', color: 'default' },
        };
        const gender = genderMap[sex] || { text: sex, color: 'default' };
        return <Tag color={gender.color}>{gender.text}</Tag>;
      },
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => role || '未分配',
    },
    {
      title: '所属机构',
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: 150,
      render: (orgName: string) => orgName || '未分配',
    },
  ];

  // 下载失败文件
  const handleDownloadFailedFile = () => {
    if (!storageVO?.id) {
      message.error('失败文件不存在');
      return;
    }

    try {
      // 构建下载URL
      const downloadUrl = buildDownloadUrl(storageVO.id);

      // 使用通用下载方法
      downloadFile(downloadUrl, '导入失败用户列表.xlsx');

      message.success('失败文件下载成功');
    } catch (error) {
      console.error('下载失败文件失败:', error);
      message.error('下载失败文件失败，请稍后重试');
    }
  };

  return (
    <Modal
      title="用户导入结果"
      open={visible}
      onCancel={onClose}
      width={1200}
      maskClosable={false}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="导入总数"
              value={Number(numberSuccesses) + Number(numberFail)}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="成功数量"
              value={Number(numberSuccesses)}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="失败数量"
              value={Number(numberFail)}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
        </Row>
      </div>

      {numberSuccesses > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4>成功导入的用户列表</h4>
          <Table
            columns={successColumns}
            dataSource={succeedList}
            rowKey={(record: any, index?: number) => `${record.userName || 'unknown'}-${index || 0}`}
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
              showQuickJumper: false,
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        </div>
      )}

      {numberFail > 0 && (
        <div>
          <h4>失败处理</h4>
          <Space>
            <span>有 {numberFail} 个用户导入失败，</span>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={handleDownloadFailedFile}
            >
              点击下载失败用户列表
            </Button>
            <span>查看失败原因并修正后重新导入。</span>
          </Space>
        </div>
      )}

      {numberSuccesses === 0 && numberFail === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <p>没有找到可导入的用户数据</p>
        </div>
      )}
    </Modal>
  );
};

export default UserImportResultModal;
